import { Search } from 'lucide-react';
import { type ChangeEvent, type ReactElement, useCallback, useEffect, useState } from 'react';

import { Button } from '@/ux-common/components/button/button';
import {
  Input,
  InputClear,
  InputField,
  InputLeadingIcon,
} from '@/ux-common/components/input/input';

import type { SearchFilterProps } from '../types';

const DEBOUNCE_MS = 200;

/**
 * Search filter input with debounced updates.
 * Matches the old data-table filter UI.
 */
export const SearchFilter = ({
  columnId,
  columnLabel,
  value,
  onChange,
  placeholder = 'Type to search...',
  onClear,
  isActive,
  onClose,
}: SearchFilterProps & { isActive: boolean; onClose: () => void }): ReactElement => {
  // Local state for immediate UI feedback
  const [localValue, setLocalValue] = useState(value);

  // Sync local value when external value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Debounced update to parent
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localValue !== value) {
        onChange(localValue);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [localValue, onChange, value]);

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  }, []);

  const handleClearInput = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setLocalValue('');
      onChange('');
    },
    [onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Close the popover if enter key is pressed
      if (e.key === 'Enter') {
        onClose();
      }
    },
    [onClose]
  );

  return (
    <>
      <div className="p-3">
        <Input className="input-reserve-clear">
          <InputField
            // Named after the column: `placeholder` is "Search..." on every filterable
            // column, so naming by it announces every filter in a table identically.
            aria-label={columnLabel ? `Search ${columnLabel}` : placeholder}
            data-testid={`search-filter-${columnId}`}
            placeholder={placeholder}
            value={localValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
          >
            <InputLeadingIcon>
              <Search />
            </InputLeadingIcon>
            {localValue && <InputClear aria-label="Clear search" onClick={handleClearInput} />}
          </InputField>
        </Input>
      </div>

      {/* Footer with Clear/Close buttons */}
      <div className="flex justify-end p-3 border-t border-border">
        {isActive && (
          <Button
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onClear();
            }}
            className="btn btn-tier3 btn-sm mr-2 font-semibold"
          >
            Clear
          </Button>
        )}
        <Button onClick={onClose} className="btn btn-tier2 btn-sm font-semibold">
          Close
        </Button>
      </div>
    </>
  );
};

SearchFilter.displayName = 'SearchFilter';
